import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { instance } from "api/apiBase";
import { getErrorMessage } from "utils/error";
import { SectionCard } from "shared/app/LeadsTableView/components/CreateLeadModal/SectionCard";
import { useFormController } from "shared/app/LeadsTableView/components/CreateLeadModal/useFormController";
import {
  DateInput,
  InputSize,
  Select,
  Show,
  TextArea
} from "shared/components";
import { LeadSource } from "views/leads-id/types";
import { AssigneeSelect } from "shared/app/LeadsTableView/slices/LeadsTable/components/AssigneeSelect";

export const DetailsSection = ({
  form,
  isOwner = false,
  propertyId,
  defaultSourceName,
  defaultSourceId: preferredSourceId
}: {
  form: ReturnType<typeof useFormController>;
  isOwner?: boolean;
  propertyId?: number;
  // Pre-selects the named source once the options load (CRM-905 in-call form).
  defaultSourceName?: string;
  // CRM-1618: pre-selects this source when it's among the options; the named
  // source is the fallback.
  defaultSourceId?: number;
}) => {
  const {
    data: sourceOptions,
    isLoading: isLoadingSources,
    isFetching: isFetchingSources,
    isError: isSourcesError
  } = useQuery({
    queryKey: ["LEAD_SOURCES"],
    queryFn: () => {
      return instance
        .get<{ items: LeadSource[] }>("/portal/v1/boom_crm/lead_sources")
        .then((res) => res.data)
        .then((data) =>
          data.items.map((source) => ({
            label: source.name,
            value: String(source.id)
          }))
        );
    },
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  // Resolve the default source by name — the id differs per environment, so a
  // caller can only ask by label — unless the caller knows the exact source
  // (CRM-1618: the called number's). Applied once per mount, and only to an
  // untouched select, so the user can still clear or change it afterwards.
  const preferredOption = preferredSourceId
    ? sourceOptions?.find(
        (option) => option.value === String(preferredSourceId)
      )
    : undefined;
  const namedOption = defaultSourceName
    ? sourceOptions?.find((option) => option.label === defaultSourceName)
    : undefined;
  // A cached list can predate the preferred source (one just created from the
  // Phone numbers screen), so wait for the mount refetch before falling back
  // to the name — the default is applied only once.
  const awaitingPreferred =
    Boolean(preferredSourceId) && !preferredOption && isFetchingSources;
  const defaultSourceId = awaitingPreferred
    ? undefined
    : (preferredOption ?? namedOption)?.value;
  const hasSelectedSource = Boolean(form.values.source_id);
  const defaultApplied = useRef(false);
  useEffect(() => {
    if (defaultApplied.current || !defaultSourceId || hasSelectedSource) return;
    defaultApplied.current = true;
    form.handleChange("source_id", () => defaultSourceId)();
    // form.handleChange is recreated every render; keying on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSourceId, hasSelectedSource]);

  const sourceError = isSourcesError
    ? "Couldn't load lead sources. Please try again."
    : form.errors.source_id;

  return (
    <SectionCard title="Details">
      <div className="flex flex-col gap-6">
        {/* Source + Assigned to. Source used to be tenant-only, on the CRM-480 premise
            that "all Owner leads will be created manually" and so had no channel to
            attribute. CRM-982 broke that premise — owner contact forms exist and set a
            lead source — and the leads table, the activity feed and the lead details
            card all show Source for owner leads, so hiding it only here meant a value
            you could see everywhere but change nowhere. It stays optional for owners
            (CRM-996 made it required for tenants only). */}
        <div
          className="grid gap-4 w-full max-w-[752px]"
          style={{ gridTemplateColumns: "repeat(2,minmax(0,1fr))" }}
        >
          <div className="w-full max-w-[368px] flex flex-col">
            <Select
              menuPosition="fixed"
              required={!isOwner}
              id="sourceEdit"
              size={InputSize.BASE}
              label="Source"
              labelClassName="mb-1.5"
              loading={isLoadingSources}
              key={sourceOptions ? "loaded" : "loading"}
              value={form.values.source_id}
              onChange={(e) =>
                form.handleChange("source_id", () => e.target.value)()
              }
              options={sourceOptions || []}
              error={sourceError}
              showErrorMessage
              legacyErrorMargins={false}
            />
          </div>
          <div className="w-full max-w-[368px] flex flex-col gap-1">
            <AssigneeSelect
              size={InputSize.BASE}
              propertyId={propertyId}
              labelClassName="mb-1.5"
              value={form.values.assignee_id}
              onChange={(assignee_id) =>
                form.handleChange("assignee_id", () => assignee_id)()
              }
              label="Assigned to"
            />
          </div>
        </div>
        {/* Desired move-in — tenant only */}
        <Show if={!isOwner}>
          <div className="w-full max-w-[368px] flex flex-col gap-1">
            <DateInput
              menuPosition="fixed"
              id="desiredMoveIn"
              label="Desired move-in"
              size={InputSize.BASE}
              value={form.values.desired_move_in_date}
              onChange={(e) => form.setDesiredMoveInDate(e.target.value)}
            />
          </div>
        </Show>
        {/* Message — tenant only */}
        <Show if={!isOwner}>
          <div className="w-full max-w-[752px]">
            <TextArea
              id="message"
              label="Message"
              labelClassName="mb-1.5"
              value={form.values.message}
              size={InputSize.BASE}
              placeholder="Message"
              onChange={(e) => form.setMessage(e.target.value)}
              autoExpand
              legacyErrorMargins={false}
              className="text-sm font-medium"
            />
          </div>
        </Show>
      </div>
    </SectionCard>
  );
};
